import type { FC } from 'react';
import React, { Fragment, useState, useCallback, useContext } from 'react';

import { Box } from '@atlaskit/primitives';

import { Attribution, ErrorBoundary, ErrorDisplay } from '@confluence/error-boundary';
import { isErrorMarkedAsHandled } from '@confluence/graphql';
import {
	HOME_RIGHT_PANEL_FORGE_EXPERIENCE,
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import type { ForgeUIExtensionType } from '@confluence/forge-ui';
import {
	FORGE_MODULE_HOMEPAGE_FEED,
	extensionTitle,
	ForgeKeyboardShortcut,
	ForgeKeyboardShortcutVisualizer,
	HomepageFeedForgeApp,
	LazyForgeUIExtensions,
} from '@confluence/forge-ui';

import { CollapsibleSection } from '../CollapsibleSection';
import { HomeWidgetState } from '../__types__/HomeWidgetsQuery';
type CollapsibleForgeAppsProps = {
	forgeApps: ReadonlyArray<ForgeUIExtensionType>;
};

const CollapsibleForgeApps: FC<CollapsibleForgeAppsProps> = ({ forgeApps }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [appInitialStates, setAppInitialStates] = useState(new Map());

	const onInitialRender = useCallback(() => {
		experienceTracker.succeed({
			name: HOME_RIGHT_PANEL_FORGE_EXPERIENCE,
		});
	}, [experienceTracker]);

	return (
		<Fragment>
			{forgeApps.map((app: ForgeUIExtensionType) => (
				<ForgeKeyboardShortcutVisualizer key={app.id} module={app}>
					<CollapsibleSection
						sectionId={app.id}
						header={extensionTitle(app)}
						initialStateOverride={appInitialStates.get(app.id)}
					>
						<ExperienceStart name={HOME_RIGHT_PANEL_FORGE_EXPERIENCE} id={app.id} />
						<Box padding="space.100">
							<HomepageFeedForgeApp
								app={app}
								extensionData={{ type: FORGE_MODULE_HOMEPAGE_FEED }}
								onInitialRender={onInitialRender}
							/>
						</Box>
					</CollapsibleSection>
					<ForgeKeyboardShortcut
						module={app}
						action={() => {
							const oldValue = appInitialStates.get(app.id);
							const newValue =
								oldValue === HomeWidgetState.EXPANDED
									? HomeWidgetState.COLLAPSED
									: HomeWidgetState.EXPANDED;
							setAppInitialStates(new Map(appInitialStates.set(app.id, newValue)));
						}}
					/>
				</ForgeKeyboardShortcutVisualizer>
			))}
		</Fragment>
	);
};

export const ForgeApps: FC<{}> = () => {
	return (
		<LazyForgeUIExtensions
			moduleType={FORGE_MODULE_HOMEPAGE_FEED}
			render={(forgeModules, loading, error) => {
				if (loading) return null;

				if (error) {
					if (!isErrorMarkedAsHandled(error)) {
						return <ErrorDisplay error={error} />;
					}
					return null;
				}

				return (
					<ErrorBoundary attribution={Attribution.DISCO}>
						<CollapsibleForgeApps forgeApps={forgeModules} />
					</ErrorBoundary>
				);
			}}
		/>
	);
};
